/** Auth contracts shared by the API and the SPA. */
import { z } from 'zod';
/**
 * Normalised at the edge so `Foo@Example.com ` and `foo@example.com` cannot
 * become two accounts. Trim first, then lowercase — the order matters when the
 * trailing space is the only difference.
 */
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.email())
    .pipe(z.string().max(254));
/**
 * Length floor, no composition rules.
 *
 * Character-class requirements measurably push people toward `Password1!` and
 * are no longer recommended by NIST. Length is what buys entropy; the upper
 * bound exists because argon2 hashing time grows with input and an unbounded
 * password is a cheap way to burn our CPU.
 *
 * Six is low, and it is a deliberate trade for signup completion in beta. What
 * makes it defensible is that guessing is rate-limited rather than merely
 * discouraged: login is capped at 10 attempts a minute per IP, password reset at
 * 5 per five minutes, and every hash is argon2id. A short password is only
 * dangerous against an attacker who can try millions of them.
 *
 * Exported so the SPA states the same number rather than its own copy of it.
 */
export const PASSWORD_MIN_LENGTH = 6;
export const passwordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(200);
export const changePasswordSchema = z.object({
    // Not `passwordSchema`: the current password was set under whatever rule
    // applied at the time, and re-validating it here would lock out anyone whose
    // password predates a change to the minimum length.
    currentPassword: z.string().min(1).max(200),
    newPassword: passwordSchema,
});
export const registerSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
});
export const loginSchema = z.object({
    email: emailSchema,
    // Deliberately not `passwordSchema`: rejecting a short password at login leaks
    // that the stored one is longer, and it locks out anyone who registered under
    // an older policy.
    password: z.string().min(1).max(200),
});
export const tokenSchema = z.object({ token: z.string().min(20).max(200) });
export const forgotPasswordSchema = z.object({ email: emailSchema });
export const resetPasswordSchema = z.object({
    token: z.string().min(20).max(200),
    password: passwordSchema,
});
export const authUserSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    emailVerified: z.boolean(),
    createdAt: z.string(),
});
/**
 * The refresh token is never in this body — it lives in an httpOnly cookie the
 * SPA cannot read. Anything returned here is safe for JavaScript to hold.
 */
export const authSessionSchema = z.object({
    accessToken: z.string(),
    expiresIn: z.number().int().positive(),
    user: authUserSchema,
});
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60;
export const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60;
export const EMAIL_VERIFICATION_TTL_SECONDS = 24 * 60 * 60;
export const PASSWORD_RESET_TTL_SECONDS = 60 * 60;
/**
 * How long an admin invite link lives.
 *
 * Deliberately much longer than a reset link. A reset is requested by someone
 * sitting at the login form right now; an invite lands in the inbox of a person
 * who did not ask for it and may not open it until next week. The two flows
 * share the token table and the same set-password screen — completing either
 * proves inbox control, which is why an invited account needs no separate
 * verification step.
 */
export const INVITE_TTL_SECONDS = 7 * 24 * 60 * 60;
export const REFRESH_COOKIE_NAME = 'gtexai_rt';
// ── Signup abuse ─────────────────────────────────────────────────────────────
/**
 * Disposable-email domains.
 *
 * A deliberately short, high-traffic list rather than a downloaded corpus of
 * thousands. The long lists are stale within months, carry false positives that
 * lock out real users, and add a supply-chain dependency to the signup path. This
 * blocks the domains that actually appear in bulk abuse; anything cleverer than
 * these is better caught by the credit ceiling than by a denylist.
 *
 * Deliberately NOT blocked: gmail/outlook subaddressing (`user+tag@`). Plenty of
 * legitimate people use it, and anyone abusing it is trivially caught by the
 * per-IP signup limit instead.
 */
export const DISPOSABLE_EMAIL_DOMAINS = new Set([
    '0-mail.com',
    '10minutemail.com',
    '20minutemail.com',
    'anonbox.net',
    'burnermail.io',
    'dispostable.com',
    'fakeinbox.com',
    'getairmail.com',
    'guerrillamail.com',
    'guerrillamail.info',
    'guerrillamail.net',
    'inboxbear.com',
    'mailcatch.com',
    'maildrop.cc',
    'mailinator.com',
    'mintemail.com',
    'mohmal.com',
    'mytemp.email',
    'sharklasers.com',
    'spam4.me',
    'temp-mail.org',
    'tempmail.com',
    'tempmailo.com',
    'throwawaymail.com',
    'trashmail.com',
    'yopmail.com',
    'yopmail.net',
]);
export function isDisposableEmail(email) {
    const domain = email.trim().toLowerCase().split('@')[1];
    if (!domain)
        return false;
    if (DISPOSABLE_EMAIL_DOMAINS.has(domain))
        return true;
    // Subdomains of a blocked domain are the same service wearing a hat.
    return [...DISPOSABLE_EMAIL_DOMAINS].some((blocked) => domain.endsWith(`.${blocked}`));
}
/**
 * Signups allowed from one address per day, above the generic rate limit.
 *
 * Twenty, not five. A single address is not a single person: offices, schools and
 * carrier-grade NAT put thousands of legitimate users behind one IP, and a limit
 * tuned for a lone abuser locks all of them out on a busy day. Twenty still makes
 * bulk account creation from one host pointless, which is the actual goal.
 */
export const SIGNUP_PER_IP = { limit: 20, windowSeconds: 24 * 3600 };
export const signupIpKey = (ip, isoDay) => `signup:ip:${ip}:${isoDay}`;
/**
 * Failed payment attempts per user, to blunt card testing.
 *
 * Card testing is a stolen-card holder using a checkout as an oracle: thousands
 * of small authorisations to find which numbers still work. The tell is a burst
 * of *failures* from one account, which is what this counts.
 */
export const PAYMENT_FAILURES = { limit: 5, windowSeconds: 3600 };
export const paymentFailureKey = (userId) => `pay:fail:${userId}`;
//# sourceMappingURL=auth.js.map